import { InboxOutlined } from '@ant-design/icons';
import { Card, Empty, Form, FormInstance, Input, Select, Space, Tag } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import React from 'react';

import { useCountry } from '../../../hooks/useCountry';
import { useIpInfo } from '../../../hooks/useIpInfo';
import { useJobTitle } from '../../../hooks/useJobTitle';
import { useLanguage } from '../../../hooks/useLanguage';

interface OnboardUserStepProps {
  form: FormInstance<any>;
  totalSteps: number;
  currentStep: number;
}

export const OnboardUserStep = ({ form, totalSteps, currentStep }: OnboardUserStepProps) => {
  const { data: languages } = useLanguage();
  const { data: jobTitles } = useJobTitle();
  const { data: countries } = useCountry();
  const { data: ipInfo } = useIpInfo();

  const [selectedLanguages, setSelectedLanguages] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (ipInfo?.country) {
      const country = countries?.find((country) => country.code === ipInfo?.country);
      if (country) {
        form.setFieldsValue({ country: country.id });
      }
    }
  }, [ipInfo]);

  return (
    <Card title="Tell us about yourself" extra={`Step ${currentStep} of ${totalSteps}`}>
      <Form.Item name="name" label="Full Name" rules={[{ required: true, type: 'string' }]}>
        <Input placeholder="Please enter your name" />
      </Form.Item>

      <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
        <Input placeholder="Please enter your email" />
      </Form.Item>

      <Form.Item name="country" label="Country" rules={[{ required: true }]}>
        <Select showSearch placeholder="Please select your country" allowClear>
          {countries?.map((country) => (
            <Select.Option key={country.id} value={country.id}>
              {country.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="jobTitle" label="Title" rules={[{ required: true }]}>
        <Select showSearch placeholder="Please select your title" allowClear>
          {jobTitles?.map((jobTitle) => (
            <Select.Option key={jobTitle.id} value={jobTitle.id}>
              {jobTitle.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="cv" label="Curriculum Vitae" rules={[{ required: true }]}>
        <Dragger name="file" action="/upload.do" multiple={false} accept=".pdf">
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag file to this area to upload</p>
          <p className="ant-upload-hint">
            PDF format is preferred, but other formats are also accepted
          </p>
        </Dragger>
      </Form.Item>

      <Card title="Languages" style={{ marginTop: 16 }}>
        <Form.Item rules={[{ required: true }]}>
          <Select
            allowClear
            mode="multiple"
            placeholder="Please select the languages you speak"
            onChange={setSelectedLanguages}>
            {languages?.map((language) => (
              <Select.Option key={language.id} value={language.id}>
                {language.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {selectedLanguages.length === 0 && (
            <Empty description="Please select at least one language above" />
          )}
          {selectedLanguages.map((lang) => (
            <Form.Item noStyle key={lang} name={lang} rules={[{ required: true }]}>
              <Select
                style={{ width: '100%' }}
                prefix={<Tag>{languages?.find((language) => language.id === lang)?.name}</Tag>}
                placeholder="Please select your level of proficiency">
                <Select.Option value="BASIC">Basic</Select.Option>
                <Select.Option value="INTERMEDIATE">Intermediate</Select.Option>
                <Select.Option value="ADVANCED">Advanced</Select.Option>
                <Select.Option value="NATIVE">Native</Select.Option>
              </Select>
            </Form.Item>
          ))}
        </Space>
      </Card>
    </Card>
  );
};
